import { App, Form, Select, Switch, TreeSelect } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crestHttp } from '../../api/client.js';
import type {
  AccessScopeConfigV2,
  AccessScopeDimension,
  AccessScopeNode,
  RoleCatalogRow,
} from './access-center-types.js';

export type ScopePolicy =
  | 'role_default'
  | 'position_org'
  | 'position_org_descendants'
  | 'explicit'
  | 'personal_region';

interface Props {
  role?: RoleCatalogRow;
  mode: 'position' | 'grant';
  scopePolicy: ScopePolicy;
  scopeConfig: AccessScopeConfigV2;
  onPolicyChange: (value: ScopePolicy) => void;
  onScopeConfigChange: (value: AccessScopeConfigV2) => void;
}

export const defaultAccessScopeConfig = (): AccessScopeConfigV2 => ({
  schemaVersion: 2,
  businessOrg: { mode: 'all', scopeType: 'crest_org', scopeIds: [], includeChildren: false },
  salesRegion: { mode: 'all', scopeType: 'sales_region', scopeIds: [], includeChildren: false },
});

export function hasInvalidSelectedScope(config: AccessScopeConfigV2): boolean {
  return [config.businessOrg, config.salesRegion].some(
    (dimension) => dimension.mode === 'selected' && dimension.scopeIds.length === 0,
  );
}

export function AccessScopeSelector(props: Props) {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [nodes, setNodes] = useState<AccessScopeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const crmRole = props.role?.domain === 'crm';

  useEffect(() => {
    if (!crmRole || props.scopePolicy !== 'explicit' || nodes.length > 0) return;
    setLoading(true);
    crestHttp
      .get<{ nodes: AccessScopeNode[] }>('/admin/access/scopes', { params: { domain: 'crm' } })
      .then(({ data }) => setNodes(data.nodes ?? []))
      .catch(() => message.error(t('scopeLoadFailed')))
      .finally(() => setLoading(false));
  }, [crmRole, message, nodes.length, props.scopePolicy, t]);

  const policies = useMemo(() => {
    const values: ScopePolicy[] =
      props.mode === 'position'
        ? [
            'role_default',
            'position_org',
            'position_org_descendants',
            'explicit',
            'personal_region',
          ]
        : ['role_default', 'explicit', 'personal_region'];
    return values.map((value) => ({ value, label: t(`scope_${value}`) }));
  }, [props.mode, t]);

  if (!crmRole) return null;
  return (
    <>
      <Form.Item label={t('scopePolicy')} required>
        <Select value={props.scopePolicy} options={policies} onChange={props.onPolicyChange} />
      </Form.Item>
      {props.scopePolicy === 'explicit' && (
        <>
          <DimensionSelector
            label={t('businessOrgScope')}
            selectLabel={t('selectBusinessOrgScope')}
            dimension={props.scopeConfig.businessOrg}
            nodes={nodes.filter((node) => node.scopeType === 'crest_org')}
            loading={loading}
            modeOptions={['all', 'selected']}
            onChange={(businessOrg) =>
              props.onScopeConfigChange({ ...props.scopeConfig, businessOrg })
            }
          />
          <DimensionSelector
            label={t('salesRegionScope')}
            selectLabel={t('selectSalesRegionScope')}
            dimension={props.scopeConfig.salesRegion}
            nodes={nodes.filter((node) => node.scopeType === 'sales_region')}
            loading={loading}
            modeOptions={['all', 'personal', 'selected']}
            onChange={(salesRegion) =>
              props.onScopeConfigChange({ ...props.scopeConfig, salesRegion })
            }
          />
        </>
      )}
    </>
  );
}

function DimensionSelector(props: {
  label: string;
  selectLabel: string;
  dimension: AccessScopeDimension;
  nodes: AccessScopeNode[];
  loading: boolean;
  modeOptions: Array<'all' | 'selected' | 'personal'>;
  onChange: (value: AccessScopeDimension) => void;
}) {
  const { t } = useTranslation('accessCenter');
  return (
    <>
      <Form.Item label={props.label} required>
        <Select
          value={props.dimension.mode}
          options={props.modeOptions.map((value) => ({ value, label: t(`scopeMode_${value}`) }))}
          onChange={(mode) =>
            props.onChange({
              ...props.dimension,
              mode,
              scopeIds: mode === 'selected' ? props.dimension.scopeIds : [],
            })
          }
        />
      </Form.Item>
      {props.dimension.mode === 'selected' && (
        <Form.Item label={props.selectLabel} required>
          <TreeSelect
            multiple
            treeData={toTreeData(props.nodes)}
            value={props.dimension.scopeIds}
            loading={props.loading}
            placeholder={props.selectLabel}
            onChange={(scopeIds) => props.onChange({ ...props.dimension, scopeIds })}
            treeNodeFilterProp="title"
            treeDefaultExpandAll
            showSearch
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
      {props.dimension.mode === 'selected' && props.dimension.scopeType === 'crest_org' && (
        <Form.Item label={t('includeChildBusinessOrgs')}>
          <Switch
            checked={props.dimension.includeChildren}
            onChange={(includeChildren) => props.onChange({ ...props.dimension, includeChildren })}
          />
        </Form.Item>
      )}
    </>
  );
}

function toTreeData(nodes: AccessScopeNode[]): Array<{
  value: string;
  title: string;
  children: ReturnType<typeof toTreeData>;
}> {
  return nodes.map((node) => ({
    value: node.scopeId,
    title: node.code ? `${node.name} (${node.code})` : node.name,
    children: toTreeData(
      (node.children ?? []).filter((child) => child.scopeType === node.scopeType),
    ),
  }));
}
